import {
  CAMPOS_COMPARADOS,
  ETIQUETA_CAMPO_COMPARADO,
  ETIQUETA_CAMPO_COMPARADO_EN,
  ETIQUETA_CAMPO_TRAMITE,
  ETIQUETA_CAMPO_TRAMITE_EN,
  ETIQUETA_TIPO_INSPECCION,
  ETIQUETA_TIPO_INSPECCION_EN,
  TIPOS_DE_INSPECCION,
  type AduanaDeExportacion,
  type CampoDeTramite,
  type CamposDeTramite,
} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router';
import {useAduanaDeExportacion, useGuardarTramite} from '../api/aduana';
import {et, tr} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_ADUANA, AYUDA_TRAMITE} from './ayuda-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

// Grupos del formulario y de la vista, en el orden del GEX.
const GRUPOS: Array<{titulo: [string, string]; campos: CampoDeTramite[]}> = [
  {titulo: ['Exportación', 'Export'], campos: ['numeroExportacion', 'tipoProducto', 'paisDestino']},
  {
    titulo: ['VUCE', 'VUCE'],
    campos: ['vuceGeneracion', 'vucePago', 'vuceRadicado', 'vuceAprobacion', 'vuceFechaAprobacion'],
  },
  {titulo: ['Aduana', 'Customs'], campos: ['agenciaAduanas', 'saeFecha', 'saeNumero']},
  {titulo: ['Reserva', 'Booking'], campos: ['agenciaCarga', 'solicitudReserva', 'fechaBooking']},
  {titulo: ['Selectividad DIAN', 'DIAN selectivity'], campos: ['selectividad', 'fechaInspeccion']},
];
const FECHAS = new Set<CampoDeTramite>([
  'vuceGeneracion',
  'vucePago',
  'vuceFechaAprobacion',
  'saeFecha',
  'solicitudReserva',
  'fechaBooking',
  'fechaInspeccion',
]);

// Tramite de aduana de una exportacion (GEX FR08, decision 0019): VUCE, SAE,
// selectividad de la DIAN, facturas y lo que el GEX dice de la logistica al
// lado de lo que tiene la app. Editores y administradores lo editan.
export function TramiteDeAduana({codigo}: {codigo: string}) {
  const consulta = useAduanaDeExportacion(codigo);
  const permisos = usePermisos();
  const [editando, setEditando] = useState(false);
  const x = consulta.data;
  const {hash} = useLocation();
  const seccion = useRef<HTMLDivElement>(null);
  // Desde la pestaña GEX se llega con #aduana: baja hasta aqui cuando cargan los datos.
  useEffect(() => {
    if (hash === '#aduana' && x) seccion.current?.scrollIntoView({block: 'start'});
  }, [hash, x]);

  return (
    <div
      ref={seccion}
      id="aduana"
      className="scroll-mt-4 rounded-lg border border-linea bg-white p-5"
      data-prueba="tramite-de-aduana"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-medium">{tr('Trámite de aduana', 'Customs filing')}</h2>
          <p className="mt-1 text-xs text-apagado">
            {tr(
              'Del cuadro de trazabilidad GEX FR08: VUCE, SAE, selectividad de la DIAN y facturas.',
              'From the GEX FR08 traceability sheet: VUCE, SAE, DIAN selectivity and invoices.',
            )}{' '}
            <Link to={`/seguimiento/gex?buscar=${encodeURIComponent(codigo)}`} className="text-verde hover:underline">
              {tr('Ver en la pestaña GEX', 'See in the GEX tab')}
            </Link>
          </p>
        </div>
        {permisos.editar && x && !editando && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
          >
            {x.tramite ? tr('✎ Editar trámite', '✎ Edit filing') : tr('+ Registrar trámite', '+ Register filing')}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-4">
        {consulta.isPending && <p className="text-sm text-apagado">{tr('Consultando…', 'Loading…')}</p>}
        {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
        {x && editando && <Formulario codigo={codigo} datos={x} onCerrar={() => setEditando(false)} />}
        {x && !editando && <Vista datos={x} />}
      </div>
    </div>
  );
}

function Vista({datos: x}: {datos: AduanaDeExportacion}) {
  const etiqueta = et(ETIQUETA_CAMPO_TRAMITE, ETIQUETA_CAMPO_TRAMITE_EN);
  const tipos = et(ETIQUETA_TIPO_INSPECCION, ETIQUETA_TIPO_INSPECCION_EN);
  const comparados = et(ETIQUETA_CAMPO_COMPARADO, ETIQUETA_CAMPO_COMPARADO_EN);
  const t = x.tramite;
  const valor = (c: CampoDeTramite) => {
    if (!t) return '—';
    const v = t[c];
    if (c === 'selectividad')
      return t.selectividad
        ? `${tipos[t.selectividad]}${t.selectividad === 'otro' && t.selectividadTexto ? ` (${t.selectividadTexto})` : ''}`
        : '—';
    return FECHAS.has(c) ? fecha(v as string | null) : textoOGuion(v as string | null);
  };
  const conGex = x.gex ? CAMPOS_COMPARADOS.filter(c => x.gex![c] || x.app[c]) : [];
  return (
    <>
      {!t && (
        <p className="text-sm text-apagado">
          {tr('Esta exportación todavía no tiene trámite de aduana.', 'This export has no customs filing yet.')}
        </p>
      )}
      {t && (
        <>
          <p className="text-xs text-apagado">
            {t.origen === 'gex_fr08'
              ? tr(
                  `Cargado del GEX${t.fila ? ` (fila ${t.fila})` : ''}.`,
                  `Loaded from the GEX${t.fila ? ` (row ${t.fila})` : ''}.`,
                )
              : tr('Registrado en la app.', 'Registered in the app.')}
            {t.enlazadaPor &&
              tr(
                ` El GEX no decía la exportación: se enlazó por ${t.enlazadaPor}.`,
                ` The GEX did not name the export: it was linked by ${t.enlazadaPor}.`,
              )}
            {t.editadoEl &&
              tr(
                ` Editado por ${t.editadoPor ?? 'alguien'} el ${fechaHora(t.editadoEl)}.`,
                ` Edited by ${t.editadoPor ?? 'someone'} on ${fechaHora(t.editadoEl)}.`,
              )}
          </p>
          {t.paraRevisar && t.nota && (
            <p className="rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-sm">
              ⚠ {tr('Para revisar:', 'To review:')} {t.nota}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {GRUPOS.map(g => (
              <div key={g.titulo[0]}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
                  {tr(g.titulo[0], g.titulo[1])}
                </h3>
                <dl className="mt-1 space-y-0.5 text-sm">
                  {g.campos.map(c => (
                    <div key={c} className="flex justify-between gap-3">
                      <dt className="text-apagado">
                        {etiqueta[c]}
                        <NotaDeAyuda titulo={etiqueta[c]} texto={AYUDA_TRAMITE[c]} />
                      </dt>
                      <dd className="text-right">{valor(c)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          {t.notas && <p className="text-sm">{t.notas}</p>}
        </>
      )}

      {x.selectividadPorContenedor.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
            {tr('Selectividad por contenedor', 'Selectivity by container')}
            <NotaDeAyuda
              titulo={tr('Selectividad por contenedor', 'Selectivity by container')}
              texto={AYUDA_ADUANA.selectividadPorContenedor}
            />
          </h3>
          <ul className="mt-1 flex flex-wrap gap-1.5 text-sm">
            {x.selectividadPorContenedor.map(s => (
              <li key={s.contenedor} className="rounded border border-linea px-1.5 py-0.5">
                <span className="font-medium">{s.contenedor}</span>: {s.tipo ? tipos[s.tipo] : textoOGuion(s.tipoTexto)}
                {s.fecha && <span className="text-apagado"> {fecha(s.fecha)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">{tr('Facturas', 'Invoices')}</h3>
        {x.facturas.length === 0 ? (
          <p className="mt-1 text-sm text-apagado">{tr('Sin facturas registradas.', 'No invoices recorded.')}</p>
        ) : (
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="py-1 font-semibold">
                  {tr('Factura', 'Invoice')}
                  <NotaDeAyuda titulo={tr('Factura', 'Invoice')} texto={AYUDA_ADUANA.facturaNumero} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Fecha', 'Date')}
                  <NotaDeAyuda titulo={tr('Fecha', 'Date')} texto={AYUDA_ADUANA.facturaFecha} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('No. EXP', 'Export no.')}
                  <NotaDeAyuda titulo={tr('No. EXP', 'Export no.')} texto={AYUDA_ADUANA.facturaExp} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Toneladas', 'Tons')}
                  <NotaDeAyuda titulo={tr('Toneladas', 'Tons')} texto={AYUDA_ADUANA.facturaToneladas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Valor', 'Value')}
                  <NotaDeAyuda titulo={tr('Valor', 'Value')} texto={AYUDA_ADUANA.facturaValor} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Origen', 'Source')}
                  <NotaDeAyuda titulo={tr('Origen', 'Source')} texto={AYUDA_ADUANA.facturaOrigen} />
                </th>
              </tr>
            </thead>
            <tbody>
              {x.facturas.map((f, i) => (
                <tr key={`${f.numero ?? 'x'}-${i}`} className="border-t border-linea/70">
                  <td className="py-1">{textoOGuion(f.numero)}</td>
                  <td className="py-1 tabular-nums">{fecha(f.fecha)}</td>
                  <td className="py-1">{textoOGuion(f.numeroExportacion)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(f.toneladas, 2)}</td>
                  <td className="py-1 text-right tabular-nums">
                    {f.valor !== null ? `${f.moneda ?? ''} ${numero(f.valor, 2)}`.trim() : textoOGuion(f.valorTexto)}
                  </td>
                  <td className="py-1 text-apagado">
                    {f.origen === 'gex_fr08' ? 'GEX' : tr('Migración / app', 'Migration / app')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {conGex.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
            {tr('Lo que dice el GEX de la logística', 'What the GEX says about logistics')}
          </h3>
          <p className="text-xs text-apagado">
            {tr(
              'La app manda: el GEX está desactualizado. Solo se usa su dato cuando la app no lo tiene.',
              'The app wins: the GEX is out of date. Its data is only used when the app has none.',
            )}
          </p>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="py-1 font-semibold">{tr('Dato', 'Field')}</th>
                <th className="py-1 font-semibold">{tr('En la app', 'In the app')}</th>
                <th className="py-1 font-semibold">{tr('En el GEX', 'In the GEX')}</th>
              </tr>
            </thead>
            <tbody>
              {conGex.map(c => {
                const esFecha = ['ingresoPuerto', 'zarpe', 'emisionBl', 'llegada'].includes(c);
                const ver = (v: string | null) => (esFecha ? fecha(v) : textoOGuion(v));
                const distinto = x.diferencias.includes(c);
                return (
                  <tr key={c} className="border-t border-linea/70">
                    <td className="py-1 text-apagado">
                      {comparados[c]}
                      <NotaDeAyuda titulo={comparados[c]} texto={AYUDA_ADUANA.comparado} />
                    </td>
                    <td className="py-1">{ver(x.app[c])}</td>
                    <td className={`py-1 ${distinto ? 'font-medium text-alerta' : ''}`}>
                      {ver(x.gex![c])}
                      {distinto && ' ≠'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Formulario({codigo, datos, onCerrar}: {codigo: string; datos: AduanaDeExportacion; onCerrar: () => void}) {
  const etiqueta = et(ETIQUETA_CAMPO_TRAMITE, ETIQUETA_CAMPO_TRAMITE_EN);
  const tipos = et(ETIQUETA_TIPO_INSPECCION, ETIQUETA_TIPO_INSPECCION_EN);
  const guardar = useGuardarTramite(codigo);
  const inicial = (c: CampoDeTramite) => (datos.tramite?.[c] as string | null | undefined) ?? '';
  const [valores, setValores] = useState<Record<CampoDeTramite, string>>(
    () =>
      Object.fromEntries(
        [...GRUPOS.flatMap(g => g.campos), 'notas'].map(c => [c, inicial(c as CampoDeTramite)]),
      ) as Record<CampoDeTramite, string>,
  );
  const [error, setError] = useState<string | null>(null);
  const enviar = async () => {
    setError(null);
    const campos: Partial<CamposDeTramite> = {};
    for (const [c, v] of Object.entries(valores) as Array<[CampoDeTramite, string]>)
      if (v !== inicial(c)) (campos as Record<string, unknown>)[c] = v.trim() || null;
    if (!Object.keys(campos).length) return onCerrar();
    try {
      await guardar.mutateAsync({version: datos.tramite?.version ?? null, campos});
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const entrada = (c: CampoDeTramite) => {
    const id = `tramite-${c}`;
    return (
      <label key={c} htmlFor={id} className="block text-xs text-apagado">
        {etiqueta[c]}
        <NotaDeAyuda titulo={etiqueta[c]} texto={AYUDA_TRAMITE[c]} dentroDeEtiqueta />
        {c === 'selectividad' ? (
          <select
            id={id}
            value={valores[c]}
            onChange={e => setValores(v => ({...v, [c]: e.target.value}))}
            className="mt-1 block w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
          >
            <option value="">—</option>
            {TIPOS_DE_INSPECCION.map(tipo => (
              <option key={tipo} value={tipo}>
                {tipos[tipo]}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            type={FECHAS.has(c) ? 'date' : 'text'}
            value={valores[c]}
            onChange={e => setValores(v => ({...v, [c]: e.target.value}))}
            className="mt-1 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta"
          />
        )}
      </label>
    );
  };
  return (
    <form
      className="space-y-4 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Editar trámite de aduana', 'Edit customs filing')}
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
    >
      {GRUPOS.map(g => (
        <fieldset key={g.titulo[0]}>
          <legend className="text-xs font-semibold uppercase tracking-wide text-apagado">
            {tr(g.titulo[0], g.titulo[1])}
          </legend>
          <div className="mt-1 grid gap-3 sm:grid-cols-3">{g.campos.map(entrada)}</div>
        </fieldset>
      ))}
      <label htmlFor="tramite-notas" className="block text-xs text-apagado">
        {etiqueta.notas}
        <NotaDeAyuda titulo={etiqueta.notas} texto={AYUDA_TRAMITE.notas} dentroDeEtiqueta />
        <textarea
          id="tramite-notas"
          value={valores.notas}
          onChange={e => setValores(v => ({...v, notas: e.target.value}))}
          rows={2}
          className="mt-1 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta"
        />
      </label>
      {error && (
        <p role="alert" className="text-sm text-alerta">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={guardar.isPending}
          className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {tr('Guardar', 'Save')}
        </button>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
        >
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}
